import { Box, Button, CircularProgress, Container, Typography } from '@mui/material'
import { BackArrow } from 'Assets/SVGs/back-arrow'
import { CAREGIVER_PORTAL_MEMBERS, CAREGIVER_PORTAL_MEMBERS_EDIT_CAREGIVER } from 'Pages/Routes'
import { generatePath, useNavigate, useParams } from 'react-router'
import { useEffect, useState } from 'react'
import { sendGet } from 'hooks/use-fetch'
import { type PortalLocation, type race } from 'core/types'
import { toast } from 'react-toastify'
import { CaregiverProfileCard } from 'Components/Cards/CaregiverPortal/CaregiverProfileCard'

export interface CaregiverMemberDetails {
  id: number
  isdid: number
  firstName: string
  middleName: string | null
  lastName: string
  dateOfBirth: Date | null
  primaryPhone: string | null
  secondaryPhone: string | null
  email: string | null
  preferredContactMethodID: number | null
  preferredContactMethod: string | null
  educationLevelID: number | null
  highestEducationLevel: string | null
  languageID: number | null
  primaryLanguage: string | null
  locationID: number | null
  location: PortalLocation | null
  livesWithCaregiverID?: number | null
  intakeContact?: string | null
  intakeName?: string | null
  raceList: race[]
  isHispanic: boolean | null
  tribalAffiliationStatus: boolean | null
  tribalAffiliationID: number | null
  tribalAffiliationName?: string | null
  hasPortalAccess: boolean
  userID: number | null
  childRelationships: Array<{ childName: string, relationshipName: string | null }>
}

export function CaregiverDisplay (): JSX.Element {
  const nav = useNavigate()
  const { caregiverId } = useParams()
  const [caregiver, setCaregiver] = useState<CaregiverMemberDetails | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const getData = async (): Promise<void> => {
      const { response, success } = await sendGet(`/CaregiverPortal/GetCaregiverDetails/${caregiverId ?? 0}`, {})
      if (success) {
        setCaregiver(response)
      } else {
        toast.error('Failed to load caregiver details')
      }
      setIsLoading(false)
    }

    void getData()
  }, [caregiverId])

  function editGuardian (): void {
    const route = generatePath(CAREGIVER_PORTAL_MEMBERS_EDIT_CAREGIVER, { caregiverId: caregiverId ?? '' })
    nav(route)
  }

  if (isLoading || caregiver === null) {
    return <CircularProgress />
  }

  return <Container sx={{ mb: '24px' }}>
    <Box sx={{ mt: '24px', mb: '24px', display: 'flex', alignItems: 'center' }}>
      <Box onClick={() => { nav(CAREGIVER_PORTAL_MEMBERS) }} data-testid='backArrow' sx={{ cursor: 'pointer', display: 'flex' }}>
        <BackArrow />
      </Box>
      <Typography variant='h5' sx={{ fontWeight: 600, ml: '5px' }} component='div'>
        {caregiver.firstName + ' ' + caregiver.lastName}
      </Typography>
    </Box>

    <CaregiverProfileCard caregiver={caregiver} />

    <Button
      variant='contained'
      className='footer-button'
      sx={{ mt: '20px' }}
      name='editDetails'
      onClick={editGuardian}
    >
      Edit Details
    </Button>
  </Container>
}
